import React, { useState, useRef, useEffect } from "react";
import { FiBell, FiHelpCircle, FiMail, FiMenu, FiX } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../Assets/images/logo-2.jpeg";
import axios from "axios";

// API Base URL - Use environment variable with fallback
import { API_BASE_URL } from "../../apiConfig";

export default function SellerNavbar() {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [userData, setUserData] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [earnings, setEarnings] = useState({ totalEarned: 0, pendingEscrow: 0, completedOrders: 0 });
  const [loading, setLoading] = useState(true);
  
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }
      
      // Fix: Better token cleaning
      let cleanToken = token;
      // Remove quotes if present
      if (cleanToken.startsWith('"') && cleanToken.endsWith('"')) {
        cleanToken = cleanToken.slice(1, -1);
      }
      if (cleanToken.startsWith("'") && cleanToken.endsWith("'")) {
        cleanToken = cleanToken.slice(1, -1);
      }
      
      console.log("Using token for API calls");
      
      // Fetch user profile data
      const [profileResponse, wizardResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/seller/profile`, {
          headers: { 
            Authorization: `Bearer ${cleanToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000,
        }).catch(error => {
          console.log('Profile fetch error:', error.response?.data || error.message);
          return { data: {} };
        }),
        
        axios.get(`${API_BASE_URL}/api/profile-wizard`, {
          headers: { 
            Authorization: `Bearer ${cleanToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000,
        }).catch(error => {
          console.log('Wizard data fetch error:', error.response?.data || error.message);
          return { data: {} };
        })
      ]);

      // Set profile image
      if (profileResponse.data && profileResponse.data.profileImage) {
        setProfileImage(profileResponse.data.profileImage);
      }

      // Set user data from localStorage or API
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          setUserData(JSON.parse(storedUser));
        } catch (e) {
          console.log("Error parsing user data:", e);
        }
      }

      if (profileResponse.data) {
        setSellerProfile(profileResponse.data);
      }

      // Fetch seller earnings from payments
      try {
        const earningsRes = await axios.get(`${API_BASE_URL}/api/payments/user-payments?role=seller`, {
          headers: { Authorization: `Bearer ${cleanToken}` },
          timeout: 30000,
        }).catch(() => ({ data: { payments: [] } }));

        const payments = earningsRes.data?.payments || [];
        let totalEarned = 0;
        let pendingEscrow = 0;
        let completedOrders = 0;

        payments.forEach((p) => {
          if (p.status === 'milestone2_paid' || p.status === 'released') {
            totalEarned += p.sellerAmount || 0;
            completedOrders += 1;
          } else if (p.status === 'milestone1_paid' || p.status === 'in_progress' || p.status === 'delivered') {
            pendingEscrow += p.milestone1Amount || 0;
          }
        });

        setEarnings({ totalEarned, pendingEscrow, completedOrders });
      } catch (err) {
        console.log('Earnings fetch error:', err.message);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/signIn');
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  // Helper function to get correct image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If it's already a full URL (starts with http), return as-is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If it starts with /, return as-is (already has correct path)
    if (imagePath.startsWith('/')) {
      return imagePath;
    }
    // Otherwise, add /api/uploads/ prefix
    return `/api/uploads/${imagePath}`;
  };

  const handleProfileImageClick = () => {
    if (profileImage) {
      return getImageUrl(profileImage);
    }
    return null;
  };

  return (
    <nav className="bg-white shadow-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/seller/dashboard" className="flex-shrink-0">
              <img src={logo} alt="JustERPs Logo" className="h-8 w-auto" />
            </Link>
            
            <div className="hidden md:ml-6 md:flex md:space-x-8">
              <Link
                to="/seller/dashboard"
                className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/seller/messages"
                className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
              >
                Messages
              </Link>
              <Link
                to="/seller/performance"
                className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
              >
                Performance
              </Link>
            </div>
          </div>

          <div className="flex items-center">
            {/* Earnings Balance */}
            <div className="mr-4 hidden sm:flex items-center gap-3 px-3 py-1.5 bg-green-50 rounded-lg border border-green-100">
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 font-medium leading-tight">Earned</span>
                <span className="text-sm font-bold text-green-700 leading-tight">
                  ${earnings.totalEarned.toFixed(2)}
                </span>
              </div>
              {earnings.pendingEscrow > 0 && (
                <div className="flex flex-col border-l border-green-200 pl-3">
                  <span className="text-[10px] text-gray-500 font-medium leading-tight">Pending</span>
                  <span className="text-sm font-bold text-orange-500 leading-tight">
                    ${earnings.pendingEscrow.toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <button className="p-2 text-gray-400 hover:text-gray-500">
              <FiBell className="h-6 w-6" />
            </button>
            
            <button className="ml-4 p-2 text-gray-400 hover:text-gray-500">
              <FiMail className="h-6 w-6" />
            </button>
            
            <div className="ml-4 relative" ref={dropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {profileImage ? (
                  <img
                    className="h-8 w-8 rounded-full"
                    src={handleProfileImageClick()}
                    alt="Profile"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-600 font-medium">
                      {userData?.username?.charAt(0) || 'U'}
                    </span>
                  </div>
                )}
              </button>
              
              {showProfileDropdown && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5">
                  <Link
                    to="/seller/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Your Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
